import {
  Box,
  Stack,
  Typography,
  Alert,
  Tabs,
  Tab,
  Pagination,
} from '@mui/material';
import { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import type { Meeting } from '@penticton-dev-events/shared';

import { PageContainer, Section } from '../components/layout';
import UpcomingMeetings from '../components/UpcomingMeetings';
import GroupList from '../features/groups/components/GroupList';
import { useAllMeetings } from '../hooks/useAllMeetings';
import { useGroups } from '../features/groups/hooks/useGroups';
import SEOHelmet from '../components/seo/SEOHelmet';
import { BrandSectionDivider } from '../components/brand';

const ITEMS_PER_PAGE = 12;

const AllEventsPage = () => {
  const navigate = useNavigate();
  const { meetings, loading: meetingsLoading, error: meetingsError } = useAllMeetings();
  const { groups, loading: groupsLoading, error: groupsError } = useGroups();
  const [activeTab, setActiveTab] = useState<'events' | 'groups'>('events');
  const [eventsPage, setEventsPage] = useState(1);
  const [groupsPage, setGroupsPage] = useState(1);

  const handleMeetingClick = (meeting: Meeting) => {
    navigate(`/meetings/${meeting.id}`);
  };

  const handleGroupClick = (group: any) => {
    navigate(`/groups/${group.id}`);
  };

  const handleTabChange = (_: React.SyntheticEvent, newValue: 'events' | 'groups') => {
    setActiveTab(newValue);
  };

  // Paginated events
  const paginatedMeetings = useMemo(() => {
    const startIndex = (eventsPage - 1) * ITEMS_PER_PAGE;
    return meetings.slice(startIndex, startIndex + ITEMS_PER_PAGE);
  }, [meetings, eventsPage]);

  const totalEventsPages = Math.ceil(meetings.length / ITEMS_PER_PAGE);

  // Paginated groups
  const paginatedGroups = useMemo(() => {
    const startIndex = (groupsPage - 1) * ITEMS_PER_PAGE;
    return groups.slice(startIndex, startIndex + ITEMS_PER_PAGE);
  }, [groups, groupsPage]);

  const totalGroupsPages = Math.ceil(groups.length / ITEMS_PER_PAGE);

  const handleEventsPageChange = (_: React.ChangeEvent<unknown>, page: number) => {
    setEventsPage(page);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleGroupsPageChange = (_: React.ChangeEvent<unknown>, page: number) => {
    setGroupsPage(page);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <PageContainer>
      <SEOHelmet
        title="All Events & Groups - Penticton.dev Events"
        description="Browse all upcoming developer events and community groups in Penticton, BC. Find meetups, workshops, and networking opportunities."
      />

      <Section variant="default">
        <Stack spacing={4}>
          {/* Page Header */}
          <Box sx={{ textAlign: 'center' }}>
            <Typography variant="h3" gutterBottom>
              Discover Events & Groups
            </Typography>
            <Typography color="text.secondary" variant="h6" paragraph>
              Explore upcoming tech events and join community groups in Penticton
            </Typography>
            <BrandSectionDivider width="150px" />
          </Box>

          {/* Tabs for Events/Groups */}
          <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
            <Tabs
              value={activeTab}
              onChange={handleTabChange}
              centered
              sx={{
                '& .MuiTab-root': {
                  fontSize: '1.1rem',
                  fontWeight: 500,
                  textTransform: 'none',
                  minWidth: 150,
                },
              }}
            >
              <Tab
                label={`Events (${meetings.length})`}
                value="events"
                id="tab-events"
                aria-controls="tabpanel-events"
              />
              <Tab
                label={`Groups (${groups.length})`}
                value="groups"
                id="tab-groups"
                aria-controls="tabpanel-groups"
              />
            </Tabs>
          </Box>

          {/* Content area with fixed height */}
          <Box sx={{ minHeight: '600px', display: 'flex', flexDirection: 'column' }}>
            {/* Events Tab Content */}
            {activeTab === 'events' && (
              <Box
                role="tabpanel"
                id="tabpanel-events"
                aria-labelledby="tab-events"
                sx={{ flex: 1, minHeight: 0 }}
              >
                {meetingsError && (
                  <Alert severity="error" sx={{ mb: 3 }}>
                    Failed to load events: {meetingsError.message}
                  </Alert>
                )}

                {meetingsLoading ? (
                  <Typography color="text.secondary" variant="h6" textAlign="center">
                    Loading events…
                  </Typography>
                ) : meetings.length === 0 ? (
                  <Box sx={{ textAlign: 'center', py: 8 }}>
                    <Typography variant="h6" color="text.secondary" gutterBottom>
                      No upcoming events scheduled
                    </Typography>
                    <Typography color="text.secondary">
                      Check back soon for new meetups and workshops!
                    </Typography>
                  </Box>
                ) : (
                  <UpcomingMeetings
                    meetings={paginatedMeetings}
                    loading={false}
                    onMeetingClick={handleMeetingClick}
                    showGroupName={true}
                    maxMeetings={paginatedMeetings.length}
                  />
                )}
              </Box>
            )}

            {/* Groups Tab Content */}
            {activeTab === 'groups' && (
              <Box
                role="tabpanel"
                id="tabpanel-groups"
                aria-labelledby="tab-groups"
                sx={{ flex: 1, minHeight: 0 }}
              >
                {groupsError && (
                  <Alert severity="error" sx={{ mb: 3 }}>
                    Failed to load groups: {groupsError.message}
                  </Alert>
                )}

                {groupsLoading ? (
                  <Typography color="text.secondary" variant="h6" textAlign="center">
                    Loading groups…
                  </Typography>
                ) : groups.length === 0 ? (
                  <Box sx={{ textAlign: 'center', py: 8 }}>
                    <Typography variant="h6" color="text.secondary" gutterBottom>
                      No groups available
                    </Typography>
                    <Typography color="text.secondary">
                      Be the first to create a community group!
                    </Typography>
                  </Box>
                ) : (
                  <GroupList groups={paginatedGroups} onSelect={handleGroupClick} />
                )}
              </Box>
            )}

            {/* Pagination - always at the bottom */}
            <Box sx={{ mt: 4, pt: 2, borderTop: 1, borderColor: 'divider' }}>
              {activeTab === 'events' && meetings.length > 0 && (
                <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                  <Pagination
                    count={totalEventsPages}
                    page={eventsPage}
                    onChange={handleEventsPageChange}
                    color="primary"
                    size="large"
                    showFirstButton
                    showLastButton
                  />
                </Box>
              )}

              {activeTab === 'groups' && groups.length > 0 && (
                <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                  <Pagination
                    count={totalGroupsPages}
                    page={groupsPage}
                    onChange={handleGroupsPageChange}
                    color="primary"
                    size="large"
                    showFirstButton
                    showLastButton
                  />
                </Box>
              )}
            </Box>
          </Box>
        </Stack>
      </Section>
    </PageContainer>
  );
};

export default AllEventsPage;
